import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MoreHorizontal, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";

export interface DetailPageAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  destructive?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  requiresConfirmation?: {
    title: string;
    description: string;
  };
  subActions?: DetailPageAction[];
  badge?: {
    variant: "default" | "secondary" | "destructive" | "outline";
  };
  isCurrent?: boolean;
}

interface StatusConfig {
  label: string;
  variant?: "default" | "secondary" | "destructive" | "outline" | "success";
}

interface BackLink {
  label: string;
  href?: string;
  onClick?: () => void;
}

interface DetailPageHeaderProps {
  title: string;
  subtitle?: string;
  status?: StatusConfig;
  backLink: BackLink;
  primaryAction?: DetailPageAction;
  secondaryActions?: DetailPageAction[];
  className?: string;
}

/**
 * DetailPageHeader - Mobile-first responsive header for detail pages
 * 
 * Features:
 * - Back navigation (always visible)
 * - Title with optional subtitle and status badge
 * - Primary action button (visible on desktop, in menu on mobile)
 * - Secondary actions in meatballs menu
 * - Confirmation dialogs for destructive actions
 * - Fully responsive - stacks on mobile, inline on desktop
 * 
 * @example
 * <DetailPageHeader
 *   title="INV-001"
 *   subtitle="Created Jan 15, 2024"
 *   status={{ label: "Paid", variant: "success" }}
 *   backLink={{ label: "Back to Invoices", href: "/invoices" }}
 *   primaryAction={{ label: "Edit", icon: Edit, onClick: handleEdit }}
 *   secondaryActions={[
 *     { label: "Send", icon: Send, onClick: handleSend },
 *     { label: "Delete", icon: Trash2, onClick: handleDelete, destructive: true, requiresConfirmation: {...} }
 *   ]}
 * />
 */
const DetailPageHeader: React.FC<DetailPageHeaderProps> = ({
  title,
  subtitle,
  status,
  backLink,
  primaryAction,
  secondaryActions = [],
  className,
}) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [confirmingAction, setConfirmingAction] = useState<DetailPageAction | null>(null);

  // Filter out hidden actions
  const visibleSecondaryActions = secondaryActions.filter(a => !a.hidden);
  
  // On mobile, primary action goes into the menu
  const mobileActions: DetailPageAction[] = isMobile && primaryAction && !primaryAction.hidden
    ? [primaryAction, ...visibleSecondaryActions]
    : visibleSecondaryActions;

  // Separate destructive actions for menu grouping
  const regularActions = mobileActions.filter(a => !a.destructive);
  const destructiveActions = mobileActions.filter(a => a.destructive);

  const handleActionClick = (action: DetailPageAction) => {
    if (action.requiresConfirmation) {
      setConfirmingAction(action);
    } else {
      action.onClick();
    }
  };

  const handleConfirm = () => {
    if (confirmingAction) {
      confirmingAction.onClick();
      setConfirmingAction(null);
    }
  };

  const hasMenuActions = mobileActions.length > 0;

  const getVariantClass = (variant?: string) => {
    switch (variant) {
      case "success":
        return "bg-success/10 text-success border-success/20";
      case "destructive":
        return "bg-destructive/10 text-destructive border-destructive/20";
      default:
        return undefined;
    }
  };

  return (
    <>
      <div className={cn("space-y-3 mb-6", className)}>
        {/* Back link + meatballs menu row */}
        <div className="flex items-center justify-between">
          {backLink.onClick ? (
            <button
              type="button"
              onClick={backLink.onClick}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{backLink.label}</span>
              <span className="sm:hidden">Back</span>
            </button>
          ) : (
            <Link
              to={backLink.href ?? "#"}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{backLink.label}</span>
              <span className="sm:hidden">Back</span>
            </Link>
          )}

          {/* Meatballs menu for secondary actions (and primary on mobile) */}
          {hasMenuActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-10 w-10">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">More actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {regularActions.map((action, index) => (
                  action.subActions && action.subActions.length > 0 ? (
                    <DropdownMenuSub key={`action-${index}`}>
                      <DropdownMenuSubTrigger disabled={action.disabled}>
                        {action.icon && <action.icon className="h-4 w-4 mr-2" />}
                        {action.label}
                      </DropdownMenuSubTrigger>
                      <DropdownMenuPortal>
                        <DropdownMenuSubContent className="w-44">
                          {action.subActions.map((sub, subIndex) => (
                            <DropdownMenuItem
                              key={`sub-${index}-${subIndex}`}
                              onClick={() => !sub.isCurrent && handleActionClick(sub)}
                              disabled={sub.disabled || sub.isCurrent}
                            >
                              {sub.badge ? (
                                <Badge variant={sub.badge.variant} className="mr-2">
                                  {sub.label}
                                </Badge>
                              ) : (
                                <>
                                  {sub.icon && <sub.icon className="h-4 w-4 mr-2" />}
                                  {sub.label}
                                </>
                              )}
                              {sub.isCurrent && '(Current)'}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuPortal>
                    </DropdownMenuSub>
                  ) : (
                    <DropdownMenuItem
                      key={`action-${index}`}
                      onClick={() => handleActionClick(action)}
                      disabled={action.disabled}
                    >
                      {action.icon && <action.icon className="h-4 w-4 mr-2" />}
                      {action.label}
                    </DropdownMenuItem>
                  )
                ))}
                
                {regularActions.length > 0 && destructiveActions.length > 0 && (
                  <DropdownMenuSeparator />
                )}
                
                {destructiveActions.map((action, index) => (
                  <DropdownMenuItem
                    key={`destructive-${index}`}
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                    className="text-destructive focus:text-destructive focus:bg-destructive/10"
                  >
                    {action.icon && <action.icon className="h-4 w-4 mr-2" />}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Header row - responsive layout */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          {/* Title section */}
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-semibold font-montserrat text-foreground truncate">
                {title}
              </h1>
              {status && (
                <Badge 
                  variant={status.variant === "success" || status.variant === "destructive" ? undefined : status.variant}
                  className={cn("shrink-0", getVariantClass(status.variant))}
                >
                  {status.label}
                </Badge>
              )}
            </div>
            {subtitle && (
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>

          {/* Actions section */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Primary action - visible on desktop only */}
            {primaryAction && !primaryAction.hidden && !isMobile && (
              <Button
                onClick={() => handleActionClick(primaryAction)}
                disabled={primaryAction.disabled}
                variant={primaryAction.destructive ? "destructive" : "default"}
                size="default"
              >
                {primaryAction.icon && <primaryAction.icon className="h-4 w-4 mr-2" />}
                {primaryAction.label}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmingAction} onOpenChange={() => setConfirmingAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmingAction?.requiresConfirmation?.title || "Confirm Action"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmingAction?.requiresConfirmation?.description || "Are you sure you want to continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={cn(
                confirmingAction?.destructive && "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              )}
            >
              {confirmingAction?.label || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default DetailPageHeader;
